import React from "react";
import { createPortal } from "react-dom";
import { MdClose, MdRestartAlt } from "react-icons/md";
import Toggle from "../Toggle/Toggle";
import styles from "./CategoryModal.module.css";
import { useCategoryModal } from "../../customHook/CategoryModal/useCategoryModal";

const CategoryModal = ({
  isOpen,
  onClose,
  onSave,
  category = null,
  existingCategories = [],
}) => {
  const {
    isEdit,
    name,
    type,
    gender,
    mode,
    isActive,
    setIsActive,
    errors,
    isSaving,
    isSubmitDisabled,
    isResetDisabled,
    isFormDirty,
    nameInputRef,
    typeSelectRef,
    genderSelectRef,
    modeSelectRef,
    handleNameChange,
    handleNameBlur,
    handleTypeChange,
    handleTypeBlur,
    handleGenderChange,
    handleGenderBlur,
    handleModeChange,
    handleModeBlur,
    handleSubmit,
    handleReset,
    handleCancel,
    handleBackdropClick,
    MAX_NAME_LENGTH,
  } = useCategoryModal({
    isOpen,
    onClose,
    onSave,
    category,
    existingCategories,
  });

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div className={styles.overlay} onClick={handleBackdropClick}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            {isEdit ? "Edit Category" : "Add New Category"}
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={handleCancel}
            aria-label="Close"
          >
            <MdClose size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className={styles.body}>
            {/* Category Name */}
            <div className={styles.fieldGroup}>
              <div className={styles.labelRow}>
                <label htmlFor="category-name" className={styles.label}>
                  Category Name <span className={styles.required}>*</span>
                </label>
                <span
                  className={`${styles.charCount} ${
                    name.length >= MAX_NAME_LENGTH ? styles.charCountWarning : ""
                  }`}
                >
                  {name.length}/{MAX_NAME_LENGTH}
                </span>
              </div>
              <input
                ref={nameInputRef}
                id="category-name"
                type="text"
                maxLength={MAX_NAME_LENGTH}
                value={name}
                onChange={handleNameChange}
                onBlur={handleNameBlur}
                placeholder="e.g. Formal Shirts, Summer Collection"
                className={`${styles.input} ${errors.name ? styles.inputError : ""}`}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? "category-name-error" : undefined}
                autoFocus
              />
              {errors.name && (
                <span id="category-name-error" className={styles.errorText} role="alert">
                  {errors.name}
                </span>
              )}
            </div>

            {/* Row: Type and Gender in same line */}
            <div className={styles.twoColRow}>
              {/* Type Field: Text field in Edit mode, Dropdown in Add mode */}
              <div className={styles.fieldGroup}>
                <label htmlFor="category-type" className={styles.label}>
                  Type <span className={styles.required}>*</span>
                </label>
                {isEdit ? (
                  <input
                    id="category-type"
                    type="text"
                    value={type ? type.charAt(0).toUpperCase() + type.slice(1).toLowerCase() : ""}
                    readOnly
                    disabled
                    className={`${styles.input} ${styles.inputReadOnly}`}
                    aria-readonly="true"
                  />
                ) : (
                  <select
                    ref={typeSelectRef}
                    id="category-type"
                    value={type}
                    onChange={handleTypeChange}
                    onBlur={handleTypeBlur}
                    className={`${styles.select} ${errors.type ? styles.inputError : ""}`}
                    aria-invalid={Boolean(errors.type)}
                    aria-describedby={errors.type ? "category-type-error" : undefined}
                  >
                    <option value="">Select Type</option>
                    <option value="filter">Filter</option>
                    <option value="collection">Collection</option>
                  </select>
                )}
                {isEdit && (
                  <span className={styles.helperText}>
                    Type cannot be changed after creation.
                  </span>
                )}
                {errors.type && (
                  <span id="category-type-error" className={styles.errorText} role="alert">
                    {errors.type}
                  </span>
                )}
              </div>

              {/* Gender Dropdown */}
              <div className={styles.fieldGroup}>
                <label htmlFor="category-gender" className={styles.label}>
                  Gender <span className={styles.required}>*</span>
                </label>
                <select
                  ref={genderSelectRef}
                  id="category-gender"
                  value={gender}
                  onChange={handleGenderChange}
                  onBlur={handleGenderBlur}
                  className={`${styles.select} ${errors.gender ? styles.inputError : ""}`}
                  aria-invalid={Boolean(errors.gender)}
                  aria-describedby={errors.gender ? "category-gender-error" : undefined}
                >
                  <option value="">Select Gender</option>
                  <option value="Women">Women</option>
                  <option value="Men">Men</option>
                </select>
                {errors.gender && (
                  <span id="category-gender-error" className={styles.errorText} role="alert">
                    {errors.gender}
                  </span>
                )}
              </div>
            </div>

            {/* Mode Dropdown */}
            <div className={styles.fieldGroup}>
              <label htmlFor="category-mode" className={styles.label}>
                Mode <span className={styles.required}>*</span>
              </label>
              <select
                ref={modeSelectRef}
                id="category-mode"
                value={mode}
                onChange={handleModeChange}
                onBlur={handleModeBlur}
                className={`${styles.select} ${errors.mode ? styles.inputError : ""}`}
                aria-invalid={Boolean(errors.mode)}
                aria-describedby={errors.mode ? "category-mode-error" : undefined}
              >
                <option value="">Select Mode</option>
                <option value="Model to Model">Model to Model</option>
                <option value="Mannequin to Model">Mannequin to Model</option>
              </select>
              {errors.mode && (
                <span id="category-mode-error" className={styles.errorText} role="alert">
                  {errors.mode}
                </span>
              )}
            </div>

            {/* Status Toggle */}
            <div className={styles.toggleRow}>
              <div className={styles.toggleText}>
                <span className={styles.toggleTitle}>Status</span>
                <span className={styles.toggleDesc}>
                  {isActive ? "Category is active and visible" : "Category is inactive and hidden"}
                </span>
              </div>
              <Toggle
                checked={isActive}
                onChange={() => setIsActive((prev) => !prev)}
              />
            </div>
          </div>

          <div className={styles.footer}>
            {isFormDirty && (
              <button
                type="button"
                onClick={handleReset}
                disabled={isSaving}
                className={`app-button-secondary ${styles.resetBtn} px-3 py-1.5 rounded-md text-xs font-semibold cursor-pointer`}
                title="Reset form"
              >
                <MdRestartAlt size={15} />
                <span>Reset</span>
              </button>
            )}
            <button
              type="button"
              onClick={handleCancel}
              className="app-button-secondary px-3 py-1.5 rounded-md text-xs font-semibold cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className={`app-button-primary ${styles.submitBtn} px-3 py-1.5 rounded-md text-xs font-semibold cursor-pointer`}
            >
              {isSaving ? "Saving..." : isEdit ? "Save Changes" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CategoryModal;
